import React from 'react'

import { Card, CardContent, Typography, Chip, Box } from '@material-ui/core'
import {
    Mail as MailIcon,
    Message as MessageSquareIcon,
} from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { Campaign, CampaignStepType } from '../../../../models/Outreach/types'

const useStyles = makeStyles((theme: Theme) => ({
    sequenceCard: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden',
        marginBottom: theme.spacing(2),
        '&:hover': {
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        },
    },
    stepIndicator: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
    },
    stepIcon: {
        padding: theme.spacing(1.5),
        borderRadius: theme.spacing(2),
        transition: 'transform 0.3s ease',
    },
    metricsContainer: {
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1.5),
        background: `linear-gradient(135deg, #f8fafc, #f1f5f9)`,
        marginTop: theme.spacing(2),
    },
    metricsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: theme.spacing(2),
        textAlign: 'center',
    },
    waitBadge: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(1.5),
    },
    tabContent: {
        width: '100%',
        paddingLeft: `calc(${theme.spacing(3)} + 24px)`,
        paddingRight: `calc(${theme.spacing(3)} + 24px)`,
    },
}))

interface SequenceStep {
    step: number
    type: 'email' | 'sms'
    subject: string
    delay: number
    sent: number
    opened: number
    clicked: number
    converted: number
}

interface MessageSequenceTabProps {
    sequence: SequenceStep[]
    campaignDetail: Campaign | null
    calculateRate: (numerator: number, denominator: number) => string
}

export const MessageSequenceTab: React.FC<MessageSequenceTabProps> = ({
    sequence,
    campaignDetail,
    calculateRate,
}) => {
    const classes = useStyles()

    return (
        <Box
            className={classes.tabContent}
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
            }}
        >
            {sequence.map((step, index) => {
                // Find the actual step data from campaignDetail
                const actualStep = campaignDetail?.steps.find(
                    (s) =>
                        (s.step_type === CampaignStepType.EMAIL ||
                            s.step_type === CampaignStepType.TEXT_MESSAGE) &&
                        s.step_order === step.step,
                )

                // Calculate step number (only counting message steps, ignoring delays)
                const stepNumber =
                    sequence
                        .filter((s) => s.type === 'email' || s.type === 'sms')
                        .findIndex((s) => s.step === step.step) + 1

                return (
                    <Box key={step.step}>
                        <Card className={classes.sequenceCard}>
                            {/* Step Indicator */}
                            <Box
                                className={classes.stepIndicator}
                                style={{
                                    backgroundColor:
                                        step.type === 'email'
                                            ? '#3b82f6'
                                            : '#10b981',
                                }}
                            />

                            <CardContent style={{ padding: 24 }}>
                                <Box
                                    display="flex"
                                    alignItems="flex-start"
                                    style={{ gap: 16 }}
                                >
                                    <Box
                                        className={classes.stepIcon}
                                        style={{
                                            backgroundColor:
                                                step.type === 'email'
                                                    ? 'rgba(59, 130, 246, 0.1)'
                                                    : 'rgba(16, 185, 129, 0.1)',
                                        }}
                                    >
                                        {step.type === 'email' ? (
                                            <MailIcon
                                                style={{
                                                    color:
                                                        step.type === 'email'
                                                            ? '#3b82f6'
                                                            : '#10b981',
                                                    fontSize: 24,
                                                }}
                                            />
                                        ) : (
                                            <MessageSquareIcon
                                                style={{
                                                    color: '#10b981',
                                                    fontSize: 24,
                                                }}
                                            />
                                        )}
                                    </Box>

                                    <Box flex={1}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            style={{
                                                gap: 12,
                                                marginBottom: 8,
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                style={{
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                Step {stepNumber}
                                            </Typography>
                                            <Chip
                                                label={step.type.toUpperCase()}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Box>

                                        {actualStep?.subject && (
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                style={{
                                                    marginBottom: 8,
                                                }}
                                            >
                                                <strong>Subject:</strong>{' '}
                                                {actualStep.subject}
                                            </Typography>
                                        )}

                                        {actualStep?.content && (
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                style={{
                                                    marginBottom: 16,
                                                    whiteSpace: 'pre-wrap',
                                                }}
                                            >
                                                {actualStep.content}
                                            </Typography>
                                        )}

                                        {/* Performance Metrics */}
                                        <Box
                                            className={classes.metricsContainer}
                                        >
                                            <Box
                                                className={classes.metricsGrid}
                                            >
                                                <Box>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        Sent
                                                    </Typography>
                                                    <Typography
                                                        variant="h6"
                                                        style={{
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        {step.sent}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    style={{
                                                        borderLeft:
                                                            '1px solid rgba(226, 232, 240, 0.5)',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        Opened
                                                    </Typography>
                                                    <Typography
                                                        variant="h6"
                                                        style={{
                                                            fontWeight: 'bold',
                                                            color: '#8b5cf6',
                                                        }}
                                                    >
                                                        {step.opened}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        {calculateRate(
                                                            step.opened,
                                                            step.sent,
                                                        )}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    style={{
                                                        borderLeft:
                                                            '1px solid rgba(226, 232, 240, 0.5)',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        Clicked
                                                    </Typography>
                                                    <Typography
                                                        variant="h6"
                                                        style={{
                                                            fontWeight: 'bold',
                                                            color: '#10b981',
                                                        }}
                                                    >
                                                        {step.clicked}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        {calculateRate(
                                                            step.clicked,
                                                            step.sent,
                                                        )}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    style={{
                                                        borderLeft:
                                                            '1px solid rgba(226, 232, 240, 0.5)',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        Converted
                                                    </Typography>
                                                    <Typography
                                                        variant="h6"
                                                        style={{
                                                            fontWeight: 'bold',
                                                            color: '#f59e0b',
                                                        }}
                                                    >
                                                        {step.converted}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        {calculateRate(
                                                            step.converted,
                                                            step.sent,
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Wait Indicator */}
                        {index < sequence.length - 1 && (
                            <Box className={classes.waitBadge}>
                                <Chip
                                    label={`Wait ${
                                        sequence[index + 1].delay
                                    } days if no response`}
                                    variant="outlined"
                                    size="small"
                                />
                            </Box>
                        )}
                    </Box>
                )
            })}
        </Box>
    )
}
